import React, { useState, useEffect } from 'react';

import {
  SearchOutlined,
  ReloadOutlined,
  PlusOutlined,
  SettingOutlined,
  HeartOutlined,
  ThunderboltOutlined,
  DollarOutlined,
  UserOutlined,
  SafetyOutlined,
  MoreOutlined,
  EyeOutlined,
  EditOutlined,
  LockOutlined,
  DeleteOutlined,
  CloudUploadOutlined,
  BarChartOutlined,
  FlagOutlined,
  DatabaseOutlined,
  ApiOutlined,
  WifiOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  ExportOutlined,
  LoginOutlined,
  PoweroffOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import {
  Table,
  Card,
  Tag,
  Button,
  Space,
  Input,
  Select,
  Dropdown,
  Modal,
  Form,
  Upload,
  Tabs,
  Progress,
  Statistic,
  Row,
  Col,
  Badge,
  Tooltip,
  Switch,
  message,
  Drawer,
  Timeline,
  Divider,
  Avatar,
} from 'antd';
import { useNavigate } from 'react-router-dom';

import TenantAnalyticsDrawer from '@/pages/SuperAdmin/components/TenantAnalyticsDrawer';
import TenantBillingDrawer from '@/pages/SuperAdmin/components/TenantBillingDrawer';
import TenantDataDrawer from '@/pages/SuperAdmin/components/TenantDataDrawer';
import TenantFeaturesDrawer from '@/pages/SuperAdmin/components/TenantFeaturesDrawer';
import TenantHealthDrawer from '@/pages/SuperAdmin/components/TenantHealthDrawer';
import TenantSecurityDrawer from '@/pages/SuperAdmin/components/TenantSecurityDrawer';
import SuperAdminAPI from '../../services/SuperAdminAPI';
import { adminPath } from '../../utils/adminPaths';
import { getTenantAppUrl } from '../../config/runtimeUrls';

const { Option } = Select;
const { TabPane } = Tabs;

const ACTIVE_LIKE_STATUSES = new Set(['active', 'trial', 'onboarding', 'overdue']);
const TENANT_STATUS_TAG_COLORS = {
  active: 'success',
  trial: 'processing',
  onboarding: 'blue',
  suspended: 'error',
  inactive: 'default',
  overdue: 'warning',
  cancelled: 'volcano',
  archived: 'purple',
  deleted: 'default',
};

const getTenantStatus = tenant => tenant?.status || (tenant?.isActive ? 'active' : 'inactive');
const getTenantStatusColor = status => TENANT_STATUS_TAG_COLORS[status] || 'default';

// 🎯 ENTERPRISE-GRADE TENANT MANAGEMENT V2
const TenantManagementV2 = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [planFilter, setPlanFilter] = useState(undefined);

  // Modals & Drawers
  const [profileModalVisible, setProfileModalVisible] = useState(false);
  const [changePlanModalVisible, setChangePlanModalVisible] = useState(false);
  const [resetPasswordModalVisible, setResetPasswordModalVisible] = useState(false);
  const [healthDrawerVisible, setHealthDrawerVisible] = useState(false);
  const [analyticsDrawerVisible, setAnalyticsDrawerVisible] = useState(false);
  const [billingDrawerVisible, setBillingDrawerVisible] = useState(false);
  const [featuresDrawerVisible, setFeaturesDrawerVisible] = useState(false);
  const [securityDrawerVisible, setSecurityDrawerVisible] = useState(false);
  const [dataDrawerVisible, setDataDrawerVisible] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);

  const [profileForm] = Form.useForm();
  const [planForm] = Form.useForm();
  const [resetForm] = Form.useForm();

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    console.log('🔍 Fetching tenants...');
    setLoading(true);
    try {
      const data = await SuperAdminAPI.listTenants();
      console.log('✅ Tenants loaded:', data);
      setTenants(data || []);
    } catch (error) {
      console.error('❌ Error fetching tenants:', error);
      message.error('Failed to load tenants');
      setTenants([]);
    } finally {
      setLoading(false);
    }
  };

  // 🏷️ 1️⃣ TENANT IDENTITY & PROFILE
  const openProfileModal = tenant => {
    setSelectedTenant(tenant);
    profileForm.setFieldsValue({
      name: tenant.name,
      subdomain: tenant.subdomain,
      plan: tenant.plan,
      contactEmail: tenant.contactEmail || '',
      contactPerson: tenant.contactPerson || '',
      supportContact: tenant.supportContact || '',
    });
    setProfileModalVisible(true);
  };

  const handleProfileUpdate = async () => {
    try {
      const values = await profileForm.validateFields();
      await SuperAdminAPI.updateTenant(selectedTenant?._id, {
        name: values.name,
        subdomain: values.subdomain,
        subscriptionTier: values.plan,
        plan: values.plan,
        contactEmail: values.contactEmail,
        contactPerson: values.contactPerson,
        supportContact: values.supportContact,
      });
      message.success('Tenant profile updated successfully');
      setProfileModalVisible(false);
      fetchTenants();
    } catch (error) {
      console.error('Profile update error:', error);
      message.error('Failed to update profile');
    }
  };

  // ❤️ 2️⃣ TENANT HEALTH & STABILITY
  const openHealthDrawer = tenant => {
    setSelectedTenant(tenant);
    setHealthDrawerVisible(true);
  };

  const renderHealthIcon = tenant => {
    const score = tenant.healthScore || 0;
    if (score >= 90) return <Badge status="success" />;
    if (score >= 70) return <Badge status="warning" />;
    return <Badge status="error" />;
  };

  // 📈 3️⃣ USAGE ANALYTICS
  const openAnalyticsDrawer = tenant => {
    setSelectedTenant(tenant);
    setAnalyticsDrawerVisible(true);
  };

  // 💳 4️⃣ BILLING INSIGHTS
  const openBillingDrawer = tenant => {
    setSelectedTenant(tenant);
    setBillingDrawerVisible(true);
  };

  // 🧰 5️⃣ FEATURE FLAGS
  const openFeaturesDrawer = tenant => {
    setSelectedTenant(tenant);
    setFeaturesDrawerVisible(true);
  };

  // 🔐 6️⃣ SECURITY & ADMIN CONTROL
  const openSecurityDrawer = tenant => {
    setSelectedTenant(tenant);
    setSecurityDrawerVisible(true);
  };

  // 📂 DATA MANAGEMENT
  const openDataDrawer = tenant => {
    setSelectedTenant(tenant);
    setDataDrawerVisible(true);
  };

  // ⚙️ 7️⃣ LIFECYCLE MANAGEMENT
  const getActionsMenu = tenant => ({
    items: [
      {
        key: 'view',
        icon: <EyeOutlined />,
        label: '👓 View Tenant Details',
        onClick: () => navigate(adminPath(`/tenants/${tenant._id}`)),
      },
      {
        key: 'login-as',
        icon: <LoginOutlined />,
        label: '🔑 Login as Tenant Admin',
        onClick: () => handleLoginAs(tenant),
      },
      { type: 'divider' },
      {
        key: 'profile',
        icon: <EditOutlined />,
        label: '✏️ Edit Profile',
        onClick: () => openProfileModal(tenant),
      },
      {
        key: 'change-plan',
        icon: <DollarOutlined />,
        label: '🧩 Change Plan',
        onClick: () => handleChangePlan(tenant),
      },
      {
        key: 'reset-password',
        icon: <LockOutlined />,
        label: '🔐 Reset Admin Password',
        onClick: () => handleResetPassword(tenant),
      },
      { type: 'divider' },
      {
        key: 'refresh-cache',
        icon: <SyncOutlined />,
        label: '🔄 Refresh Cache',
        onClick: () => handleRefreshCache(tenant),
      },
      {
        key: 'clean-orphans',
        icon: <ClearOutlined />,
        label: '🧹 Clean Orphan Files',
        onClick: () => handleCleanOrphans(tenant),
      },
      {
        key: 'export',
        icon: <ExportOutlined />,
        label: '📤 Export Tenant Data',
        onClick: () => handleExportData(tenant),
      },
      { type: 'divider' },
      {
        key: 'suspend',
        icon: <PoweroffOutlined />,
        label: ACTIVE_LIKE_STATUSES.has(getTenantStatus(tenant))
          ? '🚫 Suspend Tenant'
          : '🔁 Reactivate Tenant',
        onClick: () => handleSuspendToggle(tenant),
        danger: ACTIVE_LIKE_STATUSES.has(getTenantStatus(tenant)),
      },
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: '🗑️ Delete Tenant',
        onClick: () => handleDeleteTenant(tenant),
        danger: true,
      },
    ],
  });

  // Action handlers
  const handleLoginAs = tenant => {
    Modal.confirm({
      title: '🔑 Login as Tenant Admin',
      content: `You are about to login as admin of ${tenant.name}. Continue?`,
      onOk: async () => {
        try {
          const data = await SuperAdminAPI.impersonateTenant(tenant._id);
          if (!data?.accessToken) throw new Error('Impersonation failed');
          const tenantAppUrl = getTenantAppUrl(tenant?.slug || tenant?.subdomain);
          const ghostUrl = `${tenantAppUrl.replace(/\/$/, '')}/auth/ghost?token=${data.accessToken}`;
          message.success(`Impersonating ${tenant.name}`);
          window.open(ghostUrl, '_blank', 'noopener,noreferrer');
        } catch (e) {
          message.error(e.message || 'Failed to impersonate tenant');
        }
      },
    });
  };

  const handleChangePlan = tenant => {
    setSelectedTenant(tenant);
    planForm.setFieldsValue({ plan: tenant.plan || tenant.subscriptionTier || 'Basic' });
    setChangePlanModalVisible(true);
  };

  const handleResetPassword = tenant => {
    setSelectedTenant(tenant);
    resetForm.resetFields();
    setResetPasswordModalVisible(true);
  };

  const handleRefreshCache = tenant => {
    message.loading('Refreshing cache...', 1).then(async () => {
      try {
        await SuperAdminAPI.refreshTenantCache(tenant._id);
        message.success('Cache refreshed');
      } catch (e) {
        message.error('Failed to refresh cache');
      }
    });
  };

  const handleCleanOrphans = tenant => {
    Modal.confirm({
      title: '🧹 Clean Orphan Files',
      content: `This will remove unused files for ${tenant.name}. Continue?`,
      onOk: async () => {
        try {
          const result = await SuperAdminAPI.cleanupTenantOrphans(tenant._id);
          message.success(`Cleaned ${result.removed || 0} orphan files`);
        } catch (e) {
          message.error('Failed to clean orphans');
        }
      },
    });
  };

  const handleExportData = tenant => {
    message.loading('Exporting tenant data...', 2).then(async () => {
      try {
        const result = await SuperAdminAPI.exportTenantData(tenant._id);
        if (result.downloadUrl) {
          window.open(result.downloadUrl, '_blank');
        }
        message.success('Export complete');
      } catch (e) {
        message.error('Failed to export data');
      }
    });
  };

  const handleSuspendToggle = tenant => {
    const tenantStatus = getTenantStatus(tenant);
    const action = ACTIVE_LIKE_STATUSES.has(tenantStatus) ? 'suspend' : 'reactivate';
    Modal.confirm({
      title: action === 'suspend' ? '🚫 Suspend Tenant' : '🔁 Reactivate Tenant',
      content: `Are you sure you want to ${action} ${tenant.name}?`,
      onOk: async () => {
        try {
          if (action === 'suspend') {
            await SuperAdminAPI.updateTenantStatus(tenant._id, 'suspended', 'Admin action');
          } else {
            await SuperAdminAPI.updateTenantStatus(tenant._id, 'active', 'Admin action');
          }
          message.success(`Tenant ${action}d successfully`);
          fetchTenants();
        } catch (e) {
          message.error(`Failed to ${action} tenant`);
        }
      },
    });
  };

  const handleDeleteTenant = tenant => {
    Modal.confirm({
      title: '🗑️ Delete Tenant',
      content: (
        <div>
          <p>
            <strong>WARNING:</strong> This will permanently delete {tenant.name} and all associated
            data.
          </p>
          <p>This action cannot be undone!</p>
        </div>
      ),
      okText: 'Delete Permanently',
      okType: 'danger',
      onOk: async () => {
        try {
          await SuperAdminAPI.deleteTenant(tenant._id);
          message.success('Tenant deleted');
          fetchTenants();
        } catch (e) {
          message.error('Failed to delete tenant');
        }
      },
    });
  };

  // Filter tenants
  const filteredTenants = tenants.filter(tenant => {
    const tenantStatus = getTenantStatus(tenant);
    const matchesSearch =
      !search ||
      tenant.name?.toLowerCase().includes(search.toLowerCase()) ||
      tenant.subdomain?.toLowerCase().includes(search.toLowerCase());
    const tenantPlan = tenant.plan || tenant.subscriptionTier || 'Basic';
    const matchesStatus = !statusFilter || tenantStatus === statusFilter;
    const matchesPlan = !planFilter || tenantPlan === planFilter;
    return matchesSearch && matchesStatus && matchesPlan;
  });

  // Table columns with ALL 7 feature groups
  const columns = [
    {
      title: '🏷️ Tenant Identity',
      key: 'identity',
      width: 250,
      render: (_, tenant) => (
        <Space direction="vertical" size="small">
          <Space>
            <Avatar size="small" style={{ backgroundColor: '#1890ff' }}>
              {tenant.name?.charAt(0) || 'T'}
            </Avatar>
            <a onClick={() => navigate(adminPath(`/tenants/${tenant._id}`))}>
              <strong>{tenant.name || 'Unnamed'}</strong>
            </a>
          </Space>
          <Space size="small">
            <Tag color="blue">{tenant.plan || tenant.subscriptionTier || 'Basic'}</Tag>
            <Tag color={getTenantStatusColor(getTenantStatus(tenant))}>
              {getTenantStatus(tenant)}
            </Tag>
          </Space>
          <div style={{ fontSize: '12px', color: '#999' }}>🌐 {tenant.subdomain || '—'}</div>
        </Space>
      ),
    },
    {
      title: '❤️ Health',
      key: 'health',
      width: 100,
      align: 'center',
      render: (_, tenant) => (
        <Tooltip title="View Health Details">
          <Button
            type="text"
            icon={renderHealthIcon(tenant)}
            onClick={() => openHealthDrawer(tenant)}
          >
            {tenant.healthScore || 85}%
          </Button>
        </Tooltip>
      ),
    },
    {
      title: '👥 Users / Seats',
      key: 'users',
      width: 140,
      align: 'center',
      render: (_, tenant) => {
        const userCount = tenant.userCount || 0;
        const maxSeats = tenant.maxSeats || 5;
        const utilization = maxSeats > 0 ? (userCount / maxSeats) * 100 : 0;
        const isOverCapacity = userCount > maxSeats;
        const color = isOverCapacity ? 'error' : utilization >= 80 ? 'warning' : 'success';

        const tooltipText = isOverCapacity
          ? `⚠️ OVER CAPACITY! ${userCount} users exceeds ${maxSeats} seat limit. ${userCount - maxSeats} users over limit.`
          : `${userCount} users out of ${maxSeats} seats (${utilization.toFixed(0)}% utilized)`;

        return (
          <Tooltip title={tooltipText}>
            <Space size="small">
              <UserOutlined
                style={{ fontSize: '16px', color: isOverCapacity ? '#f5222d' : '#1890ff' }}
              />
              <Tag color={color}>
                {userCount} / {maxSeats}
              </Tag>
              {isOverCapacity && (
                <WarningOutlined
                  style={{ color: '#f5222d', fontSize: '16px' }}
                  title="Over capacity!"
                />
              )}
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: '📈 Usage',
      key: 'usage',
      width: 100,
      align: 'center',
      render: (_, tenant) => (
        <Button type="text" icon={<BarChartOutlined />} onClick={() => openAnalyticsDrawer(tenant)}>
          View
        </Button>
      ),
    },
    {
      title: '💳 Billing',
      key: 'billing',
      width: 180,
      align: 'center',
      render: (_, tenant) => {
        const pricePerSeat = tenant.pricePerSeat || 75;
        const userCount = tenant.userCount || 0;
        const currency = tenant.currency || 'KWD';
        const billingCycle = tenant.billingCycle || 'monthly';
        const totalBilling = tenant.totalBilling || pricePerSeat * userCount;

        // Calculate annual if monthly
        const displayAmount = billingCycle === 'monthly' ? totalBilling : totalBilling;
        const cycleText = billingCycle === 'monthly' ? '/ Month' : '/ Year';

        const detailText = `${pricePerSeat} ${currency} per seat × ${userCount} users ${billingCycle === 'annually' ? '× 12 months' : ''} = ${displayAmount.toFixed(2)} ${currency} ${cycleText}`;

        return (
          <Tooltip title={detailText}>
            <Button type="text" icon={<DollarOutlined />} onClick={() => openBillingDrawer(tenant)}>
              {displayAmount.toFixed(0)} {currency} {cycleText}
            </Button>
          </Tooltip>
        );
      },
    },
    {
      title: '🧰 Features',
      key: 'features',
      width: 120,
      align: 'center',
      render: (_, tenant) => {
        const enabledFeatures = tenant.enabledFeatures?.length || 0;
        const totalFeatures = 12;

        return (
          <Tooltip title={`${enabledFeatures} features enabled out of ${totalFeatures} available`}>
            <Button type="text" icon={<FlagOutlined />} onClick={() => openFeaturesDrawer(tenant)}>
              {enabledFeatures} / {totalFeatures}
            </Button>
          </Tooltip>
        );
      },
    },
    {
      title: '🔐 Security',
      key: 'security',
      width: 100,
      align: 'center',
      render: (_, tenant) => (
        <Button type="text" icon={<SafetyOutlined />} onClick={() => openSecurityDrawer(tenant)}>
          View
        </Button>
      ),
    },
    {
      title: '📂 Data',
      key: 'data',
      width: 100,
      align: 'center',
      render: (_, tenant) => (
        <Button type="text" icon={<DatabaseOutlined />} onClick={() => openDataDrawer(tenant)}>
          View
        </Button>
      ),
    },
    {
      title: '⚙️ Actions',
      key: 'actions',
      width: 80,
      fixed: 'right',
      align: 'center',
      render: (_, tenant) => (
        <Dropdown menu={getActionsMenu(tenant)} trigger={['click']}>
          <Button icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px', background: '#ffffff', minHeight: '100vh' }}>
      {/* Header */}
      <Card>
        <Row gutter={[16, 16]} align="middle">
          <Col flex="auto">
            <h2 style={{ margin: 0 }}>🏢 Tenant Management V2</h2>
            <p style={{ margin: 0, color: '#999' }}>Enterprise-Grade Multi-Tenant Administration</p>
          </Col>
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={fetchTenants} loading={loading}>
                Refresh
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate(adminPath('/tenants/new'))}
              >
                Add Tenant
              </Button>
            </Space>
          </Col>
        </Row>

        <Divider />

        {/* Filters */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8} md={6}>
            <Input
              placeholder="🔍 Search tenants..."
              prefix={<SearchOutlined />}
              value={search}
              onChange={e => setSearch(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Select
              placeholder="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="active">🟢 Active</Option>
              <Option value="trial">🔵 Trial</Option>
              <Option value="onboarding">🟣 Onboarding</Option>
              <Option value="suspended">🔴 Suspended</Option>
              <Option value="overdue">🟠 Overdue</Option>
              <Option value="cancelled">🟤 Cancelled</Option>
              <Option value="archived">🟪 Archived</Option>
              <Option value="inactive">⚪ Inactive</Option>
              <Option value="deleted">⚫ Deleted</Option>
            </Select>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Select
              placeholder="Plan"
              value={planFilter}
              onChange={setPlanFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="Basic">🧩 Basic</Option>
              <Option value="Professional">💼 Professional</Option>
              <Option value="Enterprise">🏢 Enterprise</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Change Plan Modal */}
      <Modal
        title={`Change Plan - ${selectedTenant?.name || ''}`}
        open={changePlanModalVisible}
        onOk={async () => {
          try {
            const values = await planForm.validateFields();
            await SuperAdminAPI.updateTenant(selectedTenant?._id, {
              plan: values.plan,
              subscriptionTier: values.plan,
            });
            message.success('Plan updated');
            setChangePlanModalVisible(false);
            fetchTenants();
          } catch (e) {
            message.error('Failed to update plan');
          }
        }}
        onCancel={() => setChangePlanModalVisible(false)}
      >
        <Form form={planForm} layout="vertical">
          <Form.Item name="plan" label="Plan" rules={[{ required: true }]}>
            <Select>
              <Option value="Basic">Basic</Option>
              <Option value="Professional">Professional</Option>
              <Option value="Enterprise">Enterprise</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Reset Admin Password Modal */}
      <Modal
        title={`Reset Admin Password - ${selectedTenant?.name || ''}`}
        open={resetPasswordModalVisible}
        onOk={async () => {
          try {
            const data = await SuperAdminAPI.resetTenantAdminPassword(selectedTenant?._id);
            message.success('Password reset successfully');
            Modal.info({
              title: 'Temporary Password',
              content: data?.tempPassword
                ? `Temporary password: ${data.tempPassword}`
                : 'Password reset completed',
            });
            setResetPasswordModalVisible(false);
          } catch (e) {
            message.error('Failed to reset password');
          }
        }}
        onCancel={() => setResetPasswordModalVisible(false)}
      >
        <Form form={resetForm} layout="vertical">
          <Form.Item>
            This will generate a temporary password for the tenant admin.
          </Form.Item>
        </Form>
      </Modal>

      {/* Main Table */}
      <Card style={{ marginTop: '16px' }}>
        <Table
          columns={columns}
          dataSource={filteredTenants}
          rowKey={record => record._id || record.id || Math.random()}
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: total => `Total ${total} tenants`,
          }}
          scroll={{ x: 1200 }}
          bordered
        />
      </Card>

      {/* 🏷️ Profile Modal */}
      <Modal
        title="✏️ Edit Tenant Profile"
        open={profileModalVisible}
        onOk={handleProfileUpdate}
        onCancel={() => setProfileModalVisible(false)}
        width={600}
      >
        <Form form={profileForm} layout="vertical">
          <Form.Item name="name" label="🏷️ Tenant Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="subdomain" label="🌐 Subdomain" rules={[{ required: true }]}>
            <Input addonBefore="https://" addonAfter=".law.com" />
          </Form.Item>
          <Form.Item name="plan" label="🧩 Plan" rules={[{ required: true }]}>
            <Select>
              <Option value="Basic">Basic</Option>
              <Option value="Professional">Professional</Option>
              <Option value="Enterprise">Enterprise</Option>
            </Select>
          </Form.Item>
          <Form.Item name="contactPerson" label="🪪 Contact Person">
            <Input />
          </Form.Item>
          <Form.Item name="contactEmail" label="📨 Contact Email">
            <Input type="email" />
          </Form.Item>
          <Form.Item name="supportContact" label="📞 Support Contact">
            <Input />
          </Form.Item>
          <Form.Item label="🏢 Company Logo">
            <Upload>
              <Button icon={<CloudUploadOutlined />}>Upload Logo</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      {/* ❤️ Health Drawer - ENTERPRISE-GRADE */}
      <TenantHealthDrawer
        visible={healthDrawerVisible}
        onClose={() => setHealthDrawerVisible(false)}
        tenant={selectedTenant}
      />

      {/* 📈 Analytics Drawer - ENTERPRISE-GRADE */}
      <TenantAnalyticsDrawer
        visible={analyticsDrawerVisible}
        onClose={() => setAnalyticsDrawerVisible(false)}
        tenant={selectedTenant}
      />

      {/* 💳 Billing Drawer - ENTERPRISE-GRADE */}
      <TenantBillingDrawer
        visible={billingDrawerVisible}
        onClose={() => setBillingDrawerVisible(false)}
        tenant={selectedTenant}
      />

      {/* 🧰 Features Drawer - ENTERPRISE-GRADE */}
      <TenantFeaturesDrawer
        visible={featuresDrawerVisible}
        onClose={() => setFeaturesDrawerVisible(false)}
        tenant={selectedTenant}
      />

      {/* 🔐 Security Drawer - ENTERPRISE-GRADE */}
      <TenantSecurityDrawer
        visible={securityDrawerVisible}
        onClose={() => setSecurityDrawerVisible(false)}
        tenant={selectedTenant}
      />

      {/* 📂 Data Management Drawer - ENTERPRISE-GRADE */}
      <TenantDataDrawer
        visible={dataDrawerVisible}
        onClose={() => setDataDrawerVisible(false)}
        tenant={selectedTenant}
      />
    </div>
  );
};

export default TenantManagementV2;
